import {
  Button,
  Card,
  CardContent,
  Icons,
  LoadingSpinner,
  PopoverContent,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useMemo, useState } from 'react';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { GridJobInstance, useGPSLost } from './GPSLostContext';
import { useGPSLostAddGridToMap } from './useGPSLostAddGridToMap';
import { useLengthUnits } from '../../Units/useLengthUnits';
import { useGpsLostLayerStatus } from './useGpsLostLayerStatus';
import { useShape } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';

interface GPSLostSelectSliderProps {
  configuration: any;
  isLoading: boolean;
}

export const GPSLostSelectSlider = ({
  configuration,
  isLoading,
}: GPSLostSelectSliderProps) => {
  const { messages } = useLocalization();
  const [sliderValue, setSliderValue] = useState<number[]>([0, 0]);

  const { addGridToMap, isAdding } = useGPSLostAddGridToMap();
  const { convertLength } = useLengthUnits();
  const { anyGpsLostLayers } = useGpsLostLayerStatus();

  const {
    data: shape,
    isLoading: isLoadingShape,
    isError: isErrorShape,
  } = useShape(configuration.shape_uuid);

  const { instances, selectedInstanceIndex, setSelectedInstanceIndex } =
    useGPSLost();

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <div className="w-2 h-2 rounded-full bg-green-500" />;
      case 'failed':
        return <div className="w-2 h-2 rounded-full bg-red-500" />;
      case 'running':
        return (
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
        );
      case 'created':
        return <div className="w-2 h-2 rounded-full bg-teal-500" />;
      case 'no_results':
        return <div className="w-2 h-2 rounded-full bg-muted-foreground" />;
      default:
        return <div className="w-2 h-2 rounded-full bg-blue-500" />;
    }
  };

  const getTimerangeDisplay = (
    timerange: { lower: Date; upper: Date } | undefined,
  ): string => {
    if (!timerange || !timerange.lower || !timerange.upper)
      return 'Unknown timerange';

    // Convert string dates to Date objects before formatting
    const lowerDate = formatDateTimeWithTimeZone(timerange.lower);
    const upperDate = formatDateTimeWithTimeZone(timerange.upper);

    return `${lowerDate} - ${upperDate}`;
  };

  // Function to check if an instance can be added to the map
  const canAddInstance = (instance: GridJobInstance | undefined): boolean => {
    if (!instance) return false;
    return instance.status === 'success' || instance.status === 'no_results';
  };

  const onAddInstance = async () => {
    // Get the current selected instance
    const instance = instances[selectedInstanceIndex];
    if (!instance || !canAddInstance(instance)) return;

    // Use our new hook to add the grid to the map
    await addGridToMap(configuration, instance);
  };

  const handleSliderChange = (value: number[]) => {
    if (sliderValue[0] > value[0]) {
      setSelectedInstanceIndex(value[0]);
      setSliderValue([value[0], value[0]]);
    } else {
      setSelectedInstanceIndex(value[1]);
      setSliderValue([value[1], value[1]]);
    }
  };

  // Use useMemo to memoize the min/max timeranges
  const timeranges = useMemo(() => {
    if (instances.length === 0) {
      return { min: null, max: null };
    }

    // Sort instances by timerange.lower
    const sortedInstances = [...instances]
      .filter(
        (instance) => instance.timerange?.lower && instance.timerange?.upper,
      )
      .sort((a, b) => {
        if (!a.timerange?.lower || !b.timerange?.lower) return 0;
        return a.timerange.lower.getTime() - b.timerange.lower.getTime();
      });

    if (sortedInstances.length === 0) return { min: null, max: null };

    return {
      min: sortedInstances[0]?.timerange,
      max: sortedInstances[sortedInstances.length - 1]?.timerange,
    };
  }, [instances]);

  // Format grid cell size based on user's unit preference
  const formatGridCellSize = (sizeInMeters: number): string => {
    if (!sizeInMeters) return 'Not set';

    const converted = convertLength(sizeInMeters);
    return `${converted.value} ${converted.abbreviation}`;
  };

  // Get the status text to display
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'success':
        return 'Complete - Ready to add to map';
      case 'failed':
        return 'Failed - Cannot add to map';
      case 'created':
        return 'Queued - Waiting to start processing';
      case 'running':
        return 'Processing - Please wait for completion';
      case 'no_results':
        return messages.gpsLostNoAreasFound;
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Get shape name with error handling
  const getShapeDisplay = () => {
    if (isLoadingShape) {
      return <span className="text-muted-foreground">Loading...</span>;
    }

    if (isErrorShape) {
      return <span className="text-red-500">Failed to load shape</span>;
    }

    if (!configuration.shape_uuid) {
      return messages.gpsLostNoShapeSelected;
    }

    const shapeName =
      shape?.properties?.name ||
      shape?.properties?.title ||
      `Shape ${configuration.shape_uuid.slice(0, 8)}...`;
    return shapeName;
  };

  const selectedInstance = instances[selectedInstanceIndex];

  return (
    <PopoverContent
      className="w-96 max-h-[440px] p-0 ml-6"
      side="right"
      align="start"
    >
      <div className="p-3 border-b">
        <h4 className="font-medium">{configuration.job_name}</h4>
      </div>

      {isLoading && <LoadingSpinner size={'small'} />}

      {!isLoading && instances.length > 0 && (
        <div className="p-4">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="font-medium">Shape:</div>
                <div>{getShapeDisplay()}</div>
                <div className="font-medium">Grid Cell Size:</div>
                <div>
                  {formatGridCellSize(configuration.grid_cell_size_meters)}
                </div>
                <div className="font-medium">Update Interval:</div>
                <div>
                  {configuration.update_interval_hours || 'Not set'} hours
                </div>

                <div className="font-medium">Enabled:</div>
                <div>{configuration.enabled ? 'Yes' : 'No'}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                {getStatusIcon(selectedInstance?.status || 'pending')}
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {getTimerangeDisplay(selectedInstance?.timerange)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Status:{' '}
                    {getStatusText(selectedInstance?.status || 'pending')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="py-2">
            <Slider
              value={sliderValue}
              max={instances.length - 1}
              step={1}
              onValueChange={handleSliderChange}
              className="mb-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>
                {timeranges.min
                  ? formatDateTimeWithTimeZone(timeranges.min.lower)
                  : 'N/A'}
              </span>
              <span>
                {timeranges.max
                  ? formatDateTimeWithTimeZone(timeranges.max.upper)
                  : 'N/A'}
              </span>
            </div>

            <div className="mt-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">
                      <Button
                        className="w-full"
                        onClick={onAddInstance}
                        disabled={
                          isAdding ||
                          !canAddInstance(selectedInstance) ||
                          anyGpsLostLayers
                        }
                      >
                        <div className="flex space-x-2 items-center">
                          {isAdding && (
                            <Icons.spinner className="size-4 animate-spin" />
                          )}
                          <p>
                            {isAdding
                              ? 'Adding'
                              : anyGpsLostLayers
                                ? 'GPS Lost Active'
                                : canAddInstance(selectedInstance)
                                  ? 'Add to Map'
                                  : selectedInstance?.status === 'running'
                                    ? 'Processing...'
                                    : selectedInstance?.status === 'created'
                                      ? 'Queued'
                                      : selectedInstance?.status === 'failed'
                                        ? 'Failed'
                                        : 'Not Available'}
                          </p>
                        </div>
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {anyGpsLostLayers && (
                    <TooltipContent>
                      <p>Only 1 GPS Lost can be active at a time</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      )}

      {!isLoading && instances.length === 0 && (
        <div className="p-4 text-center text-muted-foreground">
          No GPS Lost calculations available
        </div>
      )}
    </PopoverContent>
  );
};
